import React, { useEffect, useReducer, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useFlag } from '../FeatureFlag/use-flag'
import { addEventProperties, removeEventProperty, track } from '../../third-party/HeapAnalytics'
import { XP_SINGLE_PAGE_MENU } from './experiment-definitions'

const emptyExperimentsContextValues = { experiments: {} }
const fnNoOP = () => {}
const ExperimentsContext = React.createContext(emptyExperimentsContextValues)
const ExperimentsProvider = ({ children }) => {
  const [experiments, updateExperiments] = useReducer(
    (state, experiment) => ({ ...state, ...experiment }), {}
  )

  // SETUP EXPERIMENTS
  // Each must be passed the `updateExperiments` callback to update the context state
  // leaving this line here below as an example:
  useCreateExperiment(XP_SINGLE_PAGE_MENU, updateExperiments)

  const context = {
    experiments
  }

  return (
    <ExperimentsContext.Provider value={context}>
      {children}
    </ExperimentsContext.Provider>
  )
}

ExperimentsProvider.propTypes = {
  children: PropTypes.node.isRequired
}

const defaultEventOnTrack = 'Experimental treatment applied'
const defaultEventOnUntrack = 'Experimental treatment unapplied'

// eslint-disable-next-line
const useCreateExperiment = ({
  experimentName,
  flagName,
  useInitVariant,
  getTreatment = fnNoOP,
  customEventOnTrack = defaultEventOnTrack,
  customEventOnUntrack = defaultEventOnUntrack
}, updateExperiments) => {
  const [hasTracked, setHasTracked] = useState(false)
  const [hasUntracked, setHasUntracked] = useState(true)
  const flagOn = useFlag(flagName, false)
  const variant = useInitVariant()

  const variantKey = experimentName.concat('-variant')
  const treatmentKey = experimentName.concat('-treatment')

  const trackTreatment = useCallback(() => {
    if (!hasTracked) {
      setHasUntracked(false)
      track(customEventOnTrack, {
        [variantKey]: variant,
        [treatmentKey]: true
      })
      addEventProperties({
        [treatmentKey]: true
      })
      setHasTracked(true)
    }
  }, [customEventOnTrack, hasTracked, treatmentKey, variant, variantKey])

  const untrackTreatment = useCallback(() => {
    if (!hasUntracked) {
      setHasTracked(false)
      track(customEventOnUntrack, {
        [variantKey]: variant,
        [treatmentKey]: false
      })
      removeEventProperty(treatmentKey)
      setHasUntracked(true)
    }
  }, [customEventOnUntrack, hasUntracked, treatmentKey, variant, variantKey])

  const getVariant = useCallback(() => {
    return variant
  }, [variant])

  const getTreatmentAndTrack = useCallback(() => {
    trackTreatment()
    return (getTreatment && getTreatment(variant)) || null
  }, [getTreatment, variant, trackTreatment])

  const getVariantAndTrack = useCallback(() => {
    trackTreatment()
    return variant
  }, [variant, trackTreatment])

  // if the feature flag for this experiment is on
  // 1. Add experiment variant and treatment tracking events to provider
  // 2. Start tracking variant
  useEffect(() => {
    if (flagOn) {
      updateExperiments({
        [experimentName]: {
          untrackTreatment,
          getVariant,
          variant,
          getTreatmentAndTrack,
          getVariantAndTrack
        }
      })

      addEventProperties({
        [variantKey]: variant
      })
    }
  }, [experimentName, flagOn, getVariant, getTreatmentAndTrack, getVariantAndTrack, untrackTreatment, updateExperiments, variant, variantKey])
}

const ExperimentsProviderFlagged = (props) => {
  const isEnabled = useFlag('oo-v3-experiments', false)

  if (isEnabled) {
    return <ExperimentsProvider {...props} />
  }

  return (
    <ExperimentsContext.Provider value={emptyExperimentsContextValues}>
      {props.children}
    </ExperimentsContext.Provider>
  )
}

export {
  ExperimentsProviderFlagged as ExperimentsProvider
}

const emptyExperiment = {
  untrackTreatment: fnNoOP,
  getVariantAndTrack: fnNoOP,
  getTreatmentAndTrack: fnNoOP
}
export const useExperiments = (experimentKey) => {
  const ctx = React.useContext(ExperimentsContext)
  if (experimentKey === undefined) {
    return ctx
  }
  return ctx.experiments[experimentKey] || emptyExperiment
}
